/**
 * Annotation Workbench Page
 * Main interface for annotators to label tasks
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Inbox, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  AnnotationForm,
  useNextTask,
  useSkipTask,
  useSubmitLabel,
  useBatch,
} from '@/features/annotations';
import type { AnnotationTask, LabelCreate } from '@/types/annotation';
import { useI18n } from '@/i18n';

export function AnnotationWorkbenchPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get('batch') || undefined;

  const [currentTask, setCurrentTask] = useState<AnnotationTask | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [startTime, setStartTime] = useState<number>(Date.now());

  const { data: batch } = useBatch(batchId || '');
  const nextTaskMutation = useNextTask(batchId);
  const skipMutation = useSkipTask();
  const submitMutation = useSubmitLabel();

  // Fetch next task
  const fetchNextTask = useCallback(async () => {
    try {
      const task = await nextTaskMutation.mutateAsync();
      setCurrentTask(task);
      setStartTime(Date.now());
    } catch (error) {
      console.error('Failed to fetch next task:', error);
      setCurrentTask(null);
    }
  }, [nextTaskMutation]);

  // Initial load
  useEffect(() => {
    fetchNextTask();
  }, []);

  const handleSkip = async () => {
    if (!currentTask) return;

    try {
      await skipMutation.mutateAsync(currentTask.id);
      fetchNextTask();
    } catch (error) {
      console.error('Failed to skip task:', error);
    }
  };

  const handleSubmit = async (data: LabelCreate) => {
    if (!currentTask) return;

    try {
      await submitMutation.mutateAsync({
        taskId: currentTask.id,
        data,
      });
      setCompletedCount((prev) => prev + 1);
      fetchNextTask();
    } catch (error) {
      console.error('Failed to submit label:', error);
      alert(t('annotationWorkbenchPage.submitFailed') + ': ' + (error as Error).message);
    }
  };

  const isLoading = nextTaskMutation.isPending;
  const isSubmitting = submitMutation.isPending || skipMutation.isPending;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/annotation/batches')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('annotationWorkbenchPage.back')}
            </Button>
            <div className="h-6 w-px bg-gray-200" />
            <div>
              <h1 className="text-lg font-semibold text-gray-900">{t('annotationWorkbenchPage.pageTitle')}</h1>
              {batch && (
                <p className="text-sm text-gray-500">{batch.title}</p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4 text-green-500" />
              {t('annotationWorkbenchPage.completedThisSession')}: <span className="font-medium text-gray-900">{completedCount}</span>
            </div>
            {batch && (
              <div className="text-sm text-gray-600">
                {t('annotationWorkbenchPage.batchProgress')}: <span className="font-medium text-gray-900">
                  {batch.completed_tasks}/{batch.total_tasks}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6">
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4" />
              <p className="text-gray-500">{t('annotationWorkbenchPage.loadingTasks')}</p>
            </div>
          </div>
        )}

        {/* No Task Available */}
        {!isLoading && !currentTask && (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Inbox className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              {completedCount > 0 ? t('annotationWorkbenchPage.tasksCompleteTitle') : t('annotationWorkbenchPage.noTasksTitle')}
            </h2>
            <p className="text-gray-600 mb-6">
              {completedCount > 0
                ? t('annotationWorkbenchPage.completedCountMessage').replace('{count}', String(completedCount))
                : t('annotationWorkbenchPage.noTasksInBatch')}
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button variant="secondary" onClick={() => navigate('/annotation/batches')}>
                {t('annotationWorkbenchPage.returnToBatches')}
              </Button>
              <Button variant="primary" onClick={() => navigate('/annotation/stats')}>
                {t('annotationWorkbenchPage.viewStats')}
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        )}

        {/* Task Card */}
        {!isLoading && currentTask && (
          <div className="space-y-6">
            {/* Task Info */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500 font-mono">
                  {currentTask.task_id}
                </span>
                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                  {t('annotationWorkbenchPage.pendingLabel')}
                </span>
              </div>

              {/* Original Text */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-lg text-gray-900 leading-relaxed">
                  {currentTask.original_text}
                </p>
              </div>

              <div className="text-sm text-gray-500 flex items-center gap-4">
                <span>{t('annotationWorkbenchPage.wordCount')}: {currentTask.original_text.length}</span>
                {currentTask.source_session_id && (
                  <span>{t('annotationWorkbenchPage.session')}: {currentTask.source_session_id.slice(0, 8)}...</span>
                )}
              </div>
            </div>

            {/* Annotation Form */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('annotationWorkbenchPage.labelContent')}</h3>
              <AnnotationForm
                onSubmit={handleSubmit}
                onSkip={handleSkip}
                isSubmitting={isSubmitting}
                startTime={startTime}
              />
            </div>
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts Help */}
      <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg border border-gray-200 p-3 text-xs text-gray-500">
        <div className="font-medium text-gray-700 mb-1">{t('annotationWorkbenchPage.shortcuts')}</div>
        <div className="space-y-0.5">
          <div><kbd className="px-1 bg-gray-100 rounded">1</kbd> {t('annotationWorkbenchPage.positive')}</div>
          <div><kbd className="px-1 bg-gray-100 rounded">2</kbd> {t('annotationWorkbenchPage.neutral')}</div>
          <div><kbd className="px-1 bg-gray-100 rounded">3</kbd> {t('annotationWorkbenchPage.negative')}</div>
          <div><kbd className="px-1 bg-gray-100 rounded">⌘S/Ctrl+S</kbd> {t('annotationWorkbenchPage.submit')}</div>
          <div><kbd className="px-1 bg-gray-100 rounded">Esc</kbd> {t('annotationWorkbenchPage.skip')}</div>
        </div>
      </div>
    </div>
  );
}
